import { useEffect } from "react";

/**
 * useViewportInsets — single source of truth for runtime viewport math.
 *
 * iOS Safari does NOT resize the layout viewport (or `dvh`) when the software
 * keyboard appears; it overlays the keyboard on top. `position: fixed` bottom
 * CTAs therefore end up *behind* the keyboard. The only reliable signal on iOS
 * is `window.visualViewport`, whose `height`/`offsetTop` shrink while the
 * keyboard (or Safari's bottom toolbar) is showing.
 *
 * This hook listens once (mounted near the app root) and publishes CSS custom
 * properties on <html> that the whole app can consume:
 *
 *  --gg-keyboard-inset   px the keyboard overlaps the layout viewport bottom
 *                        (0 unless an editable element is focused). Fixed bottom
 *                        footers add this to their `bottom` so they ride above
 *                        the keyboard.
 *  --gg-obscured-bottom-inset
 *                        live bottom overlap (keyboard and/or Safari toolbar),
 *                        always updated. Use for locked screens that must sit
 *                        above both chrome and keyboard without waiting on
 *                        focus gating (e.g. create-giftee name footer).
 *  --gg-viewport-height  visualViewport.height in px (live), for any UI that
 *                        wants to lock to the *visible* area instead of `dvh`.
 *  --gg-viewport-offset-top  visualViewport.offsetTop in px (live). Pair with
 *                        --gg-viewport-height on a fixed shell so it covers the
 *                        visible area when iOS scrolls the layout under the
 *                        keyboard (otherwise a sage gap appears below the shell).
 *  --gg-bottom-nav-pad-bottom-effective
 *                        preserves the bottom-nav Safari-chrome behaviour that
 *                        previously lived in GifteeAppBottomNav (set to 0 when
 *                        Safari's bottom toolbar is visible so docked icons sit
 *                        flush with the visible viewport bottom).
 *
 * Centralizing this removes the duplicated visualViewport listener that used to
 * live in GifteeAppBottomNav.
 */

/** Bottom inset (px) below which we treat Safari's bottom toolbar as collapsed. */
const SAFARI_BOTTOM_CHROME_THRESHOLD_PX = 20;

function isEditableElement(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return el.isContentEditable === true;
}

function readBottomInset() {
  const vv = window.visualViewport;
  if (!vv) return 0;
  // How much of the layout viewport's bottom is currently obscured (keyboard
  // and/or Safari bottom toolbar). Matches the calc previously used by the nav.
  return Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
}

export default function useViewportInsets() {
  useEffect(() => {
    const root = document.documentElement;
    const vv = window.visualViewport;

    const applyAll = () => {
      const bottomInset = readBottomInset();
      const isMobileShell = window.matchMedia("(max-width: 500px)").matches;

      // Keyboard inset: only when something editable is focused. Without that
      // gate we'd also react to the Safari toolbar collapsing/expanding on
      // scroll, which would make fixed CTAs jump around.
      const keyboardOpen = isEditableElement(document.activeElement) && bottomInset > 1;
      root.style.setProperty("--gg-keyboard-inset", `${keyboardOpen ? bottomInset : 0}px`);
      root.style.setProperty("--gg-obscured-bottom-inset", `${Math.round(bottomInset)}px`);

      if (vv) {
        root.style.setProperty("--gg-viewport-height", `${Math.round(vv.height)}px`);
        root.style.setProperty(
          "--gg-viewport-offset-top",
          `${Math.round(Math.max(0, vv.offsetTop))}px`,
        );
      }

      // Preserve the docked bottom-nav behaviour: when Safari's bottom chrome is
      // visible on a mobile shell, drop the extra safe-area padding so icons sit
      // flush with the visible viewport bottom.
      const safariChromeVisible =
        isMobileShell && !keyboardOpen && bottomInset > SAFARI_BOTTOM_CHROME_THRESHOLD_PX;
      if (safariChromeVisible) {
        root.style.setProperty("--gg-bottom-nav-pad-bottom-effective", "0px");
      } else {
        root.style.removeProperty("--gg-bottom-nav-pad-bottom-effective");
      }
    };

    // On scroll (vv.scroll), iOS overscroll (rubber-band) temporarily makes
    // vv.offsetTop negative, which would inflate --gg-keyboard-inset and fling
    // the footer off-screen. Only update chrome-detection and viewport height
    // here — never the keyboard inset. Keyboard inset is stable: it only changes
    // when the keyboard actually opens or closes (resize/focus events).
    const applyScrollOnly = () => {
      if (vv) {
        root.style.setProperty("--gg-viewport-height", `${Math.round(vv.height)}px`);
        root.style.setProperty(
          "--gg-viewport-offset-top",
          `${Math.round(Math.max(0, vv.offsetTop))}px`,
        );
      }
      const bottomInset = readBottomInset();
      root.style.setProperty("--gg-obscured-bottom-inset", `${Math.round(bottomInset)}px`);
      const isMobileShell = window.matchMedia("(max-width: 500px)").matches;
      const keyboardOpen = isEditableElement(document.activeElement);
      const safariChromeVisible =
        isMobileShell && !keyboardOpen && bottomInset > SAFARI_BOTTOM_CHROME_THRESHOLD_PX;
      if (safariChromeVisible) {
        root.style.setProperty("--gg-bottom-nav-pad-bottom-effective", "0px");
      } else {
        root.style.removeProperty("--gg-bottom-nav-pad-bottom-effective");
      }
    };

    const scheduleAll = () => requestAnimationFrame(applyAll);
    const scheduleScrollOnly = () => requestAnimationFrame(applyScrollOnly);

    /** Skip viewport/keyboard recalc when focus moves between editables — OTP
     *  box hops were rewriting --gg-viewport-* and jittering the auth shell. */
    const onFocusOut = (e) => {
      if (isEditableElement(e.relatedTarget)) return;
      scheduleAll();
    };
    const onFocusIn = (e) => {
      if (isEditableElement(e.relatedTarget)) return;
      scheduleAll();
    };

    scheduleAll();

    // resize / focus events are the only triggers that should update the keyboard inset.
    vv?.addEventListener("resize", scheduleAll);
    window.addEventListener("resize", scheduleAll);
    window.addEventListener("orientationchange", scheduleAll);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);

    // scroll: update chrome detection + viewport height, but NOT keyboard inset.
    vv?.addEventListener("scroll", scheduleScrollOnly);
    window.addEventListener("scroll", scheduleScrollOnly);

    return () => {
      vv?.removeEventListener("resize", scheduleAll);
      window.removeEventListener("resize", scheduleAll);
      window.removeEventListener("orientationchange", scheduleAll);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      vv?.removeEventListener("scroll", scheduleScrollOnly);
      window.removeEventListener("scroll", scheduleScrollOnly);
      root.style.removeProperty("--gg-keyboard-inset");
      root.style.removeProperty("--gg-obscured-bottom-inset");
      root.style.removeProperty("--gg-viewport-height");
      root.style.removeProperty("--gg-viewport-offset-top");
      root.style.removeProperty("--gg-bottom-nav-pad-bottom-effective");
    };
  }, []);
}
